import React from 'react';
import { CheckCircle2, XCircle, CircleDot, Circle, Clock } from 'lucide-react';
import {
  isDelivered,
  isNotDelivered,
  formatTime,
  getDeliveryStopName,
} from './fleetTrackingHelpers';

// Props for FleetTimeline
export interface FleetTimelineProps {
  orders: any[];
  partOrdersEntities: Record<string, any>;
  currentStopForSelectedTour: any;
  geofenceInfoMap: Record<
    number,
    { arrival?: string; departure?: string; duration?: number }
  >;
}

// Timeline component for displaying the delivery stops and their statuses
const FleetTimeline: React.FC<FleetTimelineProps> = ({
  orders,
  partOrdersEntities,
  currentStopForSelectedTour,
  geofenceInfoMap,
}) => {
  if (!orders || orders.length === 0) {
    return (
      <p className="text-gray-500">Aucun arrêt prévu pour cette tournée.</p>
    );
  }
  return (
    <div className="space-y-0 overflow-y-auto flex-1 pr-2">
      {[...orders]
        .sort((a, b) => a.position - b.position)
        .map((delivery, index, arr) => {
          const completed = isDelivered(delivery);
          const notCompleted = isNotDelivered(delivery);
          const isCurrent =
            !completed &&
            !notCompleted &&
            currentStopForSelectedTour?.id === delivery.id;

          let iconElement;
          let iconColorClass = '';
          let bgColorClass = '';

          if (completed) {
            iconElement = <CheckCircle2 />;
            iconColorClass = 'text-green-500';
            bgColorClass = 'bg-green-100';
          } else if (notCompleted) {
            iconElement = <XCircle />;
            iconColorClass = 'text-red-500';
            bgColorClass = 'bg-red-100';
          } else if (isCurrent) {
            iconElement = <CircleDot />;
            iconColorClass = 'text-blue-500';
            bgColorClass = 'bg-blue-100';
          } else {
            iconElement = <Circle />;
            iconColorClass = 'text-gray-400';
            bgColorClass = 'bg-gray-100';
          }

          const partOrder = partOrdersEntities[delivery.partOrder];
          const infos = partOrder ? partOrder.orderInfo : delivery.infos;
          const stopName = getDeliveryStopName(infos, delivery.position);
          const stopLocation = `${infos?.city?.toUpperCase() || 'N/D'}`;

          const geofenceInfo = geofenceInfoMap[delivery.id];

          return (
            <div key={delivery.id} className="flex items-start">
              <div className="flex flex-col items-center mr-4">
                <div
                  className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${bgColorClass}`}
                >
                  {React.cloneElement(iconElement, {
                    className: `h-5 w-5 ${iconColorClass}`,
                  })}
                </div>
                {index < arr.length - 1 && (
                  <div className="w-px h-12 bg-gray-300 mt-1"></div>
                )}
              </div>
              <div className="flex-grow pb-6">
                <p
                  className={`font-semibold flex justify-around ${
                    isCurrent ? 'text-blue-600' : 'text-gray-800'
                  }`}
                >
                  {stopName}{' '}
                  <span className="text-md">
                    {partOrder?.order?.id ?? 'Chargement'}
                  </span>
                </p>
                <p className="text-sm text-gray-500">{stopLocation}</p>
                <div className="text-sm text-gray-500 mt-1 flex items-center">
                  <Clock className="h-3.5 w-3.5 mr-1.5 text-gray-400" />
                  HAE: {formatTime(delivery.deliveryTime)}
                  {completed && geofenceInfo?.departure && (
                    <span className="text-green-600 ml-1">
                      (Livré: {formatTime(geofenceInfo.departure)})
                    </span>
                  )}
                  {notCompleted && (
                    <span className="text-red-600 ml-1">(Non livré)</span>
                  )}
                </div>
                {geofenceInfo?.arrival && (
                  <div className="text-sm text-gray-500 mt-0.5">
                    Arrivée: {formatTime(geofenceInfo.arrival)}
                    {geofenceInfo.duration != null && (
                      <span> ({geofenceInfo.duration} min)</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
    </div>
  );
};

export default FleetTimeline;
